import { type ReactElement } from 'react';
import { Form, useNavigation } from 'react-router';
import { Button, type ButtonVariant } from '@marketplace/ui';
import { billingPath, billingStepPath, type CheckoutFrom } from './billing.js';

/**
 * `W5-T07` §4.3, §4.5 — the one control that sends a provider to a Stripe-hosted billing page:
 * **Subscribe** (Checkout), **Manage billing** and **Update card** (the customer portal), wherever it
 * appears.
 *
 * A form, not a link: asking for a Checkout or portal session is a write, so it is a `POST` to the
 * billing route's action, which redirects to Stripe. The action is named explicitly, because the
 * banner renders on `/account` too, whose own route does not know about billing. Disabled while the
 * router is busy, so a second click cannot ask for a second session.
 *
 * `W5-T13`: **Subscribe** names where it is (`from`), so Checkout comes back there, as
 * `ContinueWithStripe` does for Connect; without one it is the billing page's own. The registration
 * step posts to itself, so a refused Subscribe is said on the step.
 */
export function BillingButton({
  locale,
  intent,
  label,
  variant = 'primary',
  from,
}: {
  locale: string;
  intent: 'subscribe' | 'portal';
  label: string;
  variant?: ButtonVariant;
  from?: CheckoutFrom;
}): ReactElement {
  const navigation = useNavigation();
  const action = from === 'registration' ? billingStepPath(locale) : billingPath(locale);

  return (
    <Form method="post" action={action}>
      <input type="hidden" name="intent" value={intent} />
      {from === undefined ? null : <input type="hidden" name="from" value={from} />}
      <Button type="submit" variant={variant} isDisabled={navigation.state !== 'idle'}>
        {label}
      </Button>
    </Form>
  );
}
